import { Trans } from '@lingui/macro';
import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { cn } from '@shared/lib/utils';
import { Avatar, AvatarFallback, AvatarImage } from '@shared/ui/avatar';
import { Button } from '@shared/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger
} from '@shared/ui/dropdown-menu';
import { ThemeToggle } from '@shared/ui/theme-toggle';
import { LanguageSwitcher } from '@shared/i18n/LanguageSwitcher';
import { User, Settings, LogOut, Calendar, LayoutTemplate, Building, ChevronDown, Bell } from 'lucide-react';
import type { UserProfile } from '@shared/types/auth';
import { useAuth } from '@shared/hooks/useAuth';
import { useQuery } from '@tanstack/react-query';
import { filesApi } from '@shared/api/files';

interface DoctorMobileHeaderProps {
    user: UserProfile;
    className?: string;
}

/**
 * Compact mobile header for doctor portal
 * 
 * Features:
 * - Sticky positioning with backdrop blur
 * - Jema logo with gradient text
 * - User avatar dropdown with profile actions
 * - Quick access to Calendar and Templates
 * - Theme toggle and language switcher
 * - Accessible with proper ARIA labels
 */
export const DoctorMobileHeader: React.FC<DoctorMobileHeaderProps> = ({
    user,
    className
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { signOut } = useAuth();

    // Check if user is org owner
    const isOwner = user.role === 'doctor' && user.organization_roles?.includes('owner');

    // Fetch profile photo with caching
    const { data: profilePhotoUrl } = useQuery({
        queryKey: ['file-url', user.profile_image],
        queryFn: async () => {
            if (!user.profile_image) {
                return undefined;
            }
            const { url } = await filesApi.getPresignedDownloadUrl({
                storage_path: user.profile_image
            });
            return url;
        },
        enabled: Boolean(user.profile_image),
        staleTime: 5 * 60 * 1000, // 5 minutes cache
        gcTime: 10 * 60 * 1000, // 10 minutes before garbage collection
    });

    // Memoize user display info to prevent recalculations
    const userInfo = React.useMemo(() => {
        const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim();
        const displayName = fullName || user.email?.split('@')[0] || 'Doctor';
        const initials = displayName
            .split(' ')
            .map(n => n[0])
            .join('')
            .toUpperCase()
            .slice(0, 2) || 'DR';
        const fallbackAvatar = `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(displayName)}`;

        return {
            displayName,
            initials,
            email: user.email || '',
            avatarSrc: profilePhotoUrl ?? fallbackAvatar,
        };
    }, [user.first_name, user.last_name, user.email, profilePhotoUrl]);

    // Sign out handler with error handling
    const handleSignOut = React.useCallback(async () => {
        try {
            await signOut();
        } catch (error) {
            console.error('Sign out failed:', error);
            // Could show a toast notification here
        }
    }, [signOut]);

    // Navigation helper
    const handleNavigate = React.useCallback((path: string) => {
        navigate(path);
    }, [navigate]);

    // Get current page title for header
    const pageTitle = React.useMemo(() => {
        const pathMap: Record<string, string> = {
            '/dashboard': 'My Day',
            '/patients': 'Patients',
            '/quotes': 'Quotes',
            '/calendar': 'Calendar',
            '/templates': 'Templates',
            '/payments': 'Payments',
            '/messages': 'Messages',
            '/profile': 'Profile',
            '/organization/manage': 'Organization',
        };
        return pathMap[location.pathname] || null;
    }, [location.pathname]);

    return (
        <header
            className={cn(
                // Positioning
                "sticky top-0 z-40",
                // Layout
                "flex items-center justify-between gap-2",
                "h-14 px-3 sm:px-4",
                // Styling
                "bg-background/85 backdrop-blur-xl",
                "border-b border-border/40",
                // Safe area for iOS notch
                "pt-safe",
                className
            )}
            role="banner"
        >
            {/* Logo / Home link */}
            <Link
                to="/dashboard"
                className={cn(
                    "flex items-center gap-2 shrink-0",
                    "focus-visible:outline-none focus-visible:ring-2",
                    "focus-visible:ring-brand-blue focus-visible:ring-offset-2",
                    "rounded-lg -ml-1 p-1"
                )}
                aria-label="Go to dashboard"
            >
                <img
                    src="/Icon_Jema.svg"
                    alt=""
                    className="h-7 w-7 sm:h-8 sm:w-8"
                    aria-hidden="true"
                />
                <span
                    className={cn(
                        "font-bold text-base sm:text-lg",
                        "bg-gradient-to-r from-brand-blue to-brand-lime",
                        "bg-clip-text text-transparent"
                    )}
                >
                    Jema
                </span>
            </Link>

            {/* Page title - shown on larger mobile screens */}
            {pageTitle && (
                <span
                    className={cn(
                        "hidden xs:block",
                        "text-sm font-medium text-muted-foreground",
                        "truncate flex-1 text-center"
                    )}
                >
                    {pageTitle}
                </span>
            )}

            {/* Right Actions */}
            <div className="flex items-center gap-0.5 shrink-0">
                {/* Theme Toggle */}
                <ThemeToggle />

                {/* Language Switcher */}
                <LanguageSwitcher />

                {/* User Menu Dropdown */}
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button
                            variant="ghost"
                            size="sm"
                            className={cn(
                                "gap-1 pl-1 pr-1.5",
                                "h-9 min-w-9", // Touch target
                                "focus-visible:ring-brand-blue"
                            )}
                            aria-label="User menu"
                        >
                            <Avatar className="h-7 w-7 border border-border/50">
                                <AvatarImage
                                    src={userInfo.avatarSrc}
                                    alt={userInfo.displayName}
                                />
                                <AvatarFallback
                                    className={cn(
                                        "text-xs font-medium",
                                        "bg-brand-blue/10 text-brand-blue"
                                    )}
                                >
                                    {userInfo.initials}
                                </AvatarFallback>
                            </Avatar>
                            <ChevronDown
                                className="h-3 w-3 text-muted-foreground hidden sm:block"
                                aria-hidden="true"
                            />
                        </Button>
                    </DropdownMenuTrigger>

                    <DropdownMenuContent
                        align="end"
                        className="w-56"
                        sideOffset={8}
                    >
                        {/* User Info */}
                        <DropdownMenuLabel className="font-normal">
                            <div className="flex flex-col gap-1">
                                <p className="text-sm font-medium leading-none truncate">
                                    {userInfo.displayName}
                                </p>
                                <p className="text-xs text-muted-foreground leading-none truncate">
                                    {userInfo.email}
                                </p>
                            </div>
                        </DropdownMenuLabel>

                        <DropdownMenuSeparator />

                        {/* Quick Access Items (not in bottom nav) */}
                        <DropdownMenuItem
                            onClick={() => handleNavigate('/calendar')}
                            className="cursor-pointer"
                        >
                            <Calendar className="mr-2 h-4 w-4" aria-hidden="true" />
                            <Trans id="shared.layout.DoctorMobileHeader.calendar">
                                Calendar
                            </Trans>
                        </DropdownMenuItem>

                        <DropdownMenuItem
                            onClick={() => handleNavigate('/templates')}
                            className="cursor-pointer"
                        >
                            <LayoutTemplate className="mr-2 h-4 w-4" aria-hidden="true" />
                            <Trans id="shared.layout.DoctorMobileHeader.templates">
                                Templates
                            </Trans>
                        </DropdownMenuItem>

                        {isOwner && (
                            <DropdownMenuItem
                                onClick={() => handleNavigate('/organization/manage')}
                                className="cursor-pointer"
                            >
                                <Building className="mr-2 h-4 w-4" aria-hidden="true" />
                                <Trans id="shared.layout.DoctorMobileHeader.manage-organization">
                                    Manage Organization
                                </Trans>
                            </DropdownMenuItem>
                        )}

                        <DropdownMenuSeparator />

                        <DropdownMenuItem
                            onClick={() => handleNavigate('/profile')}
                            className="cursor-pointer"
                        >
                            <User className="mr-2 h-4 w-4" aria-hidden="true" />
                            <Trans id="shared.layout.DoctorMobileHeader.profile">
                                Profile
                            </Trans>
                        </DropdownMenuItem>

                        <DropdownMenuItem className="cursor-pointer">
                            <Bell className="mr-2 h-4 w-4" aria-hidden="true" />
                            <Trans id="shared.layout.DoctorMobileHeader.notifications">
                                Notifications
                            </Trans>
                        </DropdownMenuItem>

                        <DropdownMenuItem className="cursor-pointer">
                            <Settings className="mr-2 h-4 w-4" aria-hidden="true" />
                            <Trans id="shared.layout.DoctorMobileHeader.settings">
                                Settings
                            </Trans>
                        </DropdownMenuItem>

                        <DropdownMenuSeparator />

                        <DropdownMenuItem
                            onClick={handleSignOut}
                            className={cn(
                                "cursor-pointer",
                                "text-destructive focus:text-destructive",
                                "focus:bg-destructive/10"
                            )}
                        >
                            <LogOut className="mr-2 h-4 w-4" aria-hidden="true" />
                            <Trans id="shared.layout.DoctorMobileHeader.sign-out">
                                Sign out
                            </Trans>
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
        </header>
    );
};
